import React, { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AlertCircle, Loader2, X } from "lucide-react";
import { Dialog, DialogPortal } from "components/ui/dialog";
import { getReviewOfferCopy } from "./reviewOfferCopy";
import { DEFAULT_REVIEW_OFFER_PROGRAM, ReviewOfferProgram } from "./types";
import type { G2SubmitTrigger } from "./useReviewOfferState";

// G2 gives no load-failure event for a cross-origin frame, so no `onLoad` in this window means failed.
export const G2_FRAME_LOAD_TIMEOUT_MS = 20_000;

export type G2ReviewModalProps = {
  formUrl: string;
  program?: ReviewOfferProgram;
  /** true while the "submitted" call is in flight */
  submitting?: boolean;
  /** G2 did not confirm the last submit: say so and let the user try again */
  unconfirmed?: boolean;
  onSubmitted: (trigger: G2SubmitTrigger) => void;
  onClose: () => void;
  onOpened?: () => void;
};

/**
 * S2 for the G2 embed (review_offer_g2_embed). Shows G2's own review form in a
 * wide modal. The FTC incentive disclosure sits above the frame so it is
 * visible before submit. G2 sends no completion message, but a successful
 * submit navigates the frame, so the 2nd iframe load means "submitted"; the
 * footer button stays as a fallback. Either only asks the server, which
 * grants once G2's review_status confirms the review.
 */
export const G2ReviewModal = ({
  formUrl,
  program = DEFAULT_REVIEW_OFFER_PROGRAM,
  submitting = false,
  unconfirmed = false,
  onSubmitted,
  onClose,
  onOpened,
}: G2ReviewModalProps) => {
  const copy = getReviewOfferCopy(program);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // Bumped by Retry to remount the iframe.
  const [attempt, setAttempt] = useState(0);
  // Load #1 = form opened; load #2+ = G2 navigated after a successful submit.
  const loadCountRef = useRef(0);
  const autoSubmittedRef = useRef(false);
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  useEffect(() => {
    onOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unconfirmed: the navigation we saw was not a submit, so arm auto-detect again.
  useEffect(() => {
    if (!unconfirmed) return;
    autoSubmittedRef.current = false;
    setAutoSubmitted(false);
  }, [unconfirmed]);

  useEffect(() => {
    if (loaded || failed) return undefined;
    const timer = setTimeout(() => setFailed(true), G2_FRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [loaded, failed, attempt]);

  const retry = () => {
    setLoaded(false);
    setFailed(false);
    setAttempt((n) => n + 1);
    loadCountRef.current = 0;
    autoSubmittedRef.current = false;
    setAutoSubmitted(false);
  };

  const handleFrameLoad = () => {
    setLoaded(true);
    loadCountRef.current += 1;
    if (loadCountRef.current < 2 || autoSubmittedRef.current) return;
    autoSubmittedRef.current = true;
    setAutoSubmitted(true);
    onSubmitted("auto");
  };

  return (
    // modal={false}: a modal Dialog traps focus and would fight the drawer's own focus trap (same as ExpandMapModal).
    <Dialog open onOpenChange={(next) => !next && onClose()} modal={false}>
      <DialogPortal>
        {/* Radix renders no Overlay when modal={false}, so the backdrop is a plain div. */}
        <div className="fixed inset-0 z-[1400] bg-black/70 backdrop-blur-md" aria-hidden="true" />
        <DialogPrimitive.Content
          // cb-v2-root: the builder scopes box-sizing/border/font under this class (Tailwind preflight is off).
          className="cb-v2-root fixed left-1/2 top-1/2 z-[1400] flex h-[90vh] w-[95vw] max-w-[1100px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-solid border-border bg-background shadow-xl focus:outline-none"
          aria-describedby={undefined}
          // Non-modal Radix dismisses on outside pointer/focus, and focus moving into the
          // cross-origin G2 frame can count as outside — only Close/Escape may dismiss a half-written review.
          onInteractOutside={(event) => event.preventDefault()}
          data-cypress-id="reviewOfferG2Modal"
        >
          <div className="flex items-start justify-between gap-4 border-0 border-b border-solid border-border px-6 py-4">
            <div className="flex flex-col gap-1">
              <DialogPrimitive.Title className="text-base font-semibold text-foreground">
                Write a review on G2
              </DialogPrimitive.Title>
              <p
                className="m-0 text-[13px] leading-5 text-muted-foreground"
                data-cypress-id="reviewOfferG2IncentiveDisclosure"
              >
                {copy.review.incentiveDisclosure}
              </p>
            </div>
            <DialogPrimitive.Close
              aria-label="Close"
              className="shrink-0 rounded-sm text-muted-foreground opacity-70 transition-opacity hover:opacity-100"
              data-cypress-id="reviewOfferG2Close"
            >
              <X className="size-5" />
            </DialogPrimitive.Close>
          </div>

          <div className="relative min-h-0 flex-1 bg-white">
            {!failed && (
              <iframe
                key={attempt}
                title="Write a review on G2"
                src={formUrl}
                onLoad={handleFrameLoad}
                className="h-full w-full border-0"
                data-cypress-id="reviewOfferG2Frame"
              />
            )}
            {!loaded && !failed && (
              <div className="absolute inset-0 flex items-center justify-center bg-background">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {failed && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background">
                <AlertCircle className="size-6 text-destructive" />
                <span className="text-sm text-destructive">We couldn&apos;t load the review form.</span>
                <button
                  type="button"
                  onClick={retry}
                  className="h-9 rounded-lg border border-solid border-destructive/40 bg-background px-4 text-[13.5px] font-semibold text-destructive hover:bg-red-50 dark:hover:bg-red-950/30"
                  data-cypress-id="reviewOfferG2Retry"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4 border-0 border-t border-solid border-border px-6 py-3">
            <span className="text-[12.5px] text-muted-foreground" data-cypress-id="reviewOfferG2FooterHint">
              {autoSubmitted
                ? "Thanks! Applying your discount…"
                : unconfirmed
                ? "We couldn't find your review on G2 yet. Finish and submit the form, then confirm again."
                : "Finished on G2? Confirm below to apply your discount."}
            </span>
            <button
              type="button"
              onClick={() => onSubmitted("button")}
              disabled={!loaded || failed || submitting || autoSubmitted}
              className="inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border border-solid border-[rgba(10,113,150,0.4)] bg-background px-4 text-[13.5px] font-semibold text-[#0A7196] shadow-[0_1px_2px_0_rgba(2,6,24,0.06)] hover:bg-[#F2FBFE] disabled:cursor-not-allowed disabled:opacity-60 dark:border-[rgba(63,199,250,0.4)] dark:text-[#3FC7FA] dark:hover:bg-white/5"
              data-cypress-id="reviewOfferG2SubmittedButton"
            >
              {(submitting || autoSubmitted) && <Loader2 className="size-4 animate-spin" />}I&apos;ve submitted my
              review
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
